import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";

const PAGE_LABELS = {
  "/": "Home",
  "/design": "Design Studio",
  "/book": "Book a Fitting",
  "/weddings": "Weddings",
};

const pageLabel = (p) => PAGE_LABELS[p] || p;

export default function TrafficTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/admin/analytics")
      .then((r) => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const days = useMemo(() => {
    if (!data) return [];
    const map = Object.fromEntries(data.daily.map((d) => [d.date, d]));
    const out = [];
    for (let i = 13; i >= 0; i--) {
      const dt = new Date(Date.now() - i * 86400000);
      const key = dt.toLocaleDateString("en-CA", { timeZone: "America/Los_Angeles" });
      out.push({ date: key, views: map[key]?.views || 0, visitors: map[key]?.visitors || 0 });
    }
    return out;
  }, [data]);

  if (loading) {
    return <p className="mt-16 text-center font-mono text-xs uppercase tracking-[0.3em] text-[#858078]" data-testid="admin-traffic-loading">Loading traffic…</p>;
  }
  if (!data) {
    return <p className="mt-16 text-center font-mono text-xs uppercase tracking-[0.3em] text-[#858078]">Could not load analytics</p>;
  }

  const maxViews = Math.max(1, ...days.map((d) => d.views));
  const maxPage = Math.max(1, ...(data.pages.map((p) => p.views) || [1]));
  const maxSource = Math.max(1, ...(data.sources.map((s) => s.visitors) || [1]));

  const stats = [
    ["Visitors Today", data.totals.visitors_today],
    ["Views Today", data.totals.views_today],
    ["Visitors · 30 Days", data.totals.visitors_30d],
    ["Views · 30 Days", data.totals.views_30d],
  ];

  return (
    <div className="mt-10" data-testid="admin-traffic-tab">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {stats.map(([label, value]) => (
          <div key={label} className="border border-[#2A2825] bg-[#1A1917] p-5" data-testid={`traffic-stat-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
            <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#858078]">{label}</p>
            <p className="mt-2 font-serif text-3xl font-light text-[#FFFFFF]">{value}</p>
          </div>
        ))}
      </div>

      <div className="mt-8 border border-[#2A2825] bg-[#1A1917] p-6">
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#858078]">Daily views — last 14 days</p>
        <div className="mt-6 flex h-40 items-end gap-1.5" data-testid="traffic-daily-chart">
          {days.map((d) => (
            <div key={d.date} className="group relative flex-1">
              <div
                className="w-full bg-[#C59B27]/80 transition-colors group-hover:bg-[#C59B27]"
                style={{ height: `${Math.max(2, (d.views / maxViews) * 150)}px` }}
                title={`${d.date}: ${d.views} views · ${d.visitors} visitors`}
              />
            </div>
          ))}
        </div>
        <div className="mt-2 flex justify-between font-mono text-[9px] uppercase tracking-[0.15em] text-[#524F4A]">
          <span>{days[0]?.date.slice(5)}</span>
          <span>{days[days.length - 1]?.date.slice(5)} (today)</span>
        </div>
      </div>

      <div className="mt-8 grid gap-8 lg:grid-cols-2">
        <div className="border border-[#2A2825] bg-[#1A1917] p-6" data-testid="traffic-pages">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#858078]">Most viewed pages · 30 days</p>
          <div className="mt-5 space-y-4">
            {data.pages.length === 0 && <p className="text-sm text-[#524F4A]">No visits recorded yet.</p>}
            {data.pages.map((p) => (
              <div key={p.path}>
                <div className="flex items-baseline justify-between text-sm">
                  <span className="text-[#FFFFFF]">{pageLabel(p.path)}</span>
                  <span className="font-mono text-xs text-[#C59B27]">{p.views}</span>
                </div>
                <div className="mt-1.5 h-1 bg-[#2A2825]">
                  <div className="h-1 bg-[#C59B27]/70" style={{ width: `${(p.views / maxPage) * 100}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="border border-[#2A2825] bg-[#1A1917] p-6" data-testid="traffic-sources">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#858078]">Where visitors come from · 30 days</p>
          <div className="mt-5 space-y-4">
            {data.sources.length === 0 && <p className="text-sm text-[#524F4A]">No visits recorded yet.</p>}
            {data.sources.map((s) => (
              <div key={s.source}>
                <div className="flex items-baseline justify-between text-sm">
                  <span className="text-[#FFFFFF]">{s.source}</span>
                  <span className="font-mono text-xs text-[#C59B27]">{s.visitors} visitor{s.visitors === 1 ? "" : "s"}</span>
                </div>
                <div className="mt-1.5 h-1 bg-[#2A2825]">
                  <div className="h-1 bg-[#C59B27]/70" style={{ width: `${(s.visitors / maxSource) * 100}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <p className="mt-6 text-xs font-light text-[#524F4A]">
        Counts are anonymous — no personal information is collected from visitors who only browse.
        Your own visits to the admin dashboard are not counted.
      </p>
    </div>
  );
}
